import {useMemo} from 'react';

import {
  useEntityDynamicConfigQuery,
  useOrganizationDyamicConfigQuery,
  useUserDynamicConfigQuery,
  useViewerOrganizationsDynamicConfigQuery,
} from '../generated/graphql';

export type DynamicConfig = {
  config?: object;
  loading: boolean;
};

export type DynamicConfigID =
  | 'rquitt_test_dynamic_config'
  | 'line_plot_increased_max_runs'
  | 'full_screen_line_plot_increased_max_runs'
  | 'history_line_feature_flags';

export const useUserDynamicConfigWithLoading = (
  configId: DynamicConfigID
): DynamicConfig => {
  const {data, loading} = useUserDynamicConfigQuery({
    variables: {configId: configId},
  });

  if (!data?.viewer?.dynamicConfig) {
    return {config: undefined, loading};
  }

  return {config: data.viewer.dynamicConfig.value, loading};
};

export const useEntityDynamicConfigWithLoading = (
  configId: DynamicConfigID,
  entityName: string
): DynamicConfig => {
  const {data, loading} = useEntityDynamicConfigQuery({
    variables: {configId: configId, entityName: entityName},
  });

  if (!data?.entity?.dynamicConfig) {
    return {config: undefined, loading};
  }

  return {config: data.entity.dynamicConfig.value, loading};
};

export const useOrganizationDynamicConfigWithLoading = (
  configId: DynamicConfigID,
  orgName: string
): DynamicConfig => {
  const {data, loading} = useOrganizationDyamicConfigQuery({
    variables: {configId: configId, orgName: orgName},
  });

  if (!data?.organization?.dynamicConfig) {
    return {config: undefined, loading};
  }

  return {config: data.organization.dynamicConfig.value, loading};
};

export type BooleanDynamicConfig = {
  value: boolean;
  loading: boolean;
  error?: Error;
};

/**
 * Fetches a dynamic config from all the viewer's organizations, extracts
 * a single boolean field by `key`, and returns true if ANY org has it enabled.
 */
export const useViewerOrgsBooleanDynamicConfig = (
  configId: DynamicConfigID,
  key: string
): BooleanDynamicConfig => {
  const {data, loading, error} = useViewerOrganizationsDynamicConfigQuery({
    variables: {configId},
  });

  const orgs = data?.viewer?.organizations;

  const value = useMemo(() => {
    if (!orgs) {
      return false;
    }
    return orgs.some(org => org.dynamicConfig?.value?.[key] === true);
  }, [orgs, key]);

  return useMemo(() => ({value, loading, error}), [value, loading, error]);
};

/**
 * Fetches a dynamic config from all the viewer's organizations, extracts
 * string values for `key`, and returns the set of distinct values that
 * appear in any org and are listed in `validValues` (including
 * `defaultValue` when an org explicitly sets it).
 *
 * Non-strings and strings not in `validValues` are ignored. When multiple orgs
 * disagree, primacy is left to the caller.
 */
export type StringDynamicConfig<T extends string = string> = {
  values: ReadonlySet<T>;
  loading: boolean;
  error?: Error;
};

function isValidValue<T extends string>(
  value: string,
  validValues: ReadonlySet<T>
): value is T {
  return validValues.has(value as T);
}

export const useViewerOrgsStringDynamicConfig = <T extends string>(
  configId: DynamicConfigID,
  key: string,
  validValues: ReadonlySet<T>
): StringDynamicConfig<T> => {
  const {data, loading, error} = useViewerOrganizationsDynamicConfigQuery({
    variables: {configId},
  });

  const orgs = data?.viewer?.organizations;

  const values = useMemo(() => {
    const out = new Set<T>();
    if (!orgs) {
      return out as ReadonlySet<T>;
    }
    for (const org of orgs) {
      const val = org.dynamicConfig?.value?.[key];

      if (typeof val === 'string' && isValidValue(val, validValues)) {
        out.add(val);
      }
    }
    return out as ReadonlySet<T>;
  }, [orgs, key, validValues]);

  return useMemo(() => ({values, loading, error}), [values, loading, error]);
};

/**
 * Fetches a dynamic config from all the viewer's organizations, extracts
 * a single numeric field by `key`, and aggregates across orgs using the
 * given strategy.
 *
 * - `'max'`: take the highest value across all orgs.
 * - `'min'`: take the lowest value across all orgs.
 *
 * Orgs where the field is missing or non-numeric are skipped. A warning is
 * logged when a non-numeric value is encountered for the expected key.
 */
export type NumericDynamicConfig = {
  value?: number;
  loading: boolean;
  error?: Error;
};
export type NumericMergeStrategy = 'min' | 'max';
export const useViewerOrgsNumericDynamicConfig = (
  configId: DynamicConfigID,
  key: string,
  strategy: NumericMergeStrategy
): NumericDynamicConfig => {
  const {data, loading, error} = useViewerOrganizationsDynamicConfigQuery({
    variables: {configId},
  });

  const orgs = data?.viewer?.organizations;

  const value = useMemo(() => {
    if (!orgs) {
      return undefined;
    }

    const values = orgs.reduce<number[]>((acc, org) => {
      const val = org.dynamicConfig?.value?.[key];
      if (typeof val === 'number') {
        acc.push(val);
      } else if (val != null) {
        console.warn(
          `[useDynamicConfig] Expected numeric value for key "${key}" in org "${org.name}", got ${typeof val}. Value ignored.`
        );
      }
      return acc;
    }, []);

    if (values.length === 0) {
      return undefined;
    }

    switch (strategy) {
      case 'max':
        return Math.max(...values);
      case 'min':
        return Math.min(...values);
    }
  }, [orgs, key, strategy]);

  return useMemo(() => ({value, loading, error}), [value, loading, error]);
};
